/**
 * useIdleTimer — listens to admin activity and fires onWarning / onTimeout
 * after the configured idle threshold.
 *
 * Activity events tracked: mousemove, keydown, click, scroll, touchstart.
 * Events are attached to the window with `passive: true` (no input delay).
 *
 * Contract:
 *   • `timeoutMs`     — total idle window before the lock fires.
 *   • `warningMs`     — how long before timeout to fire `onWarning`. Default 60s.
 *   • `enabled`       — set false to fully disengage (e.g. on /admin/login).
 *   • `onWarning`     — called once when the warning window enters; receives
 *                       `(remainingMs)`.
 *   • `onTimeout`     — called once when the full idle window elapses.
 *   • Returns `{ reset, getLastActivity }` so a "Stay logged in" button can
 *     reset the timer manually.
 *
 * Implementation notes:
 *   • A single setTimeout drives the warning callback; another drives the
 *     timeout callback. Both are torn down + re-armed on any activity.
 *   • To avoid thrashing the event loop with mousemove, we throttle the
 *     timer reset to once per 500ms.
 */
import { useCallback, useEffect, useRef } from "react";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const RESET_THROTTLE_MS = 500;

export function useIdleTimer({
  timeoutMs,
  warningMs = 60_000,
  enabled = true,
  onWarning,
  onTimeout,
}) {
  const warnTimer = useRef(null);
  const lockTimer = useRef(null);
  const lastActivity = useRef(Date.now());
  const lastReset = useRef(0);
  const warned = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimer.current) { clearTimeout(warnTimer.current); warnTimer.current = null; }
    if (lockTimer.current) { clearTimeout(lockTimer.current); lockTimer.current = null; }
  }, []);

  const arm = useCallback(() => {
    clearTimers();
    if (!enabled || !timeoutMs || timeoutMs <= 0) return;
    warned.current = false;
    const warnIn = Math.max(0, timeoutMs - warningMs);
    warnTimer.current = setTimeout(() => {
      warned.current = true;
      if (onWarning) onWarning(warningMs);
    }, warnIn);
    lockTimer.current = setTimeout(() => {
      if (onTimeout) onTimeout();
    }, timeoutMs);
  }, [clearTimers, enabled, timeoutMs, warningMs, onWarning, onTimeout]);

  const reset = useCallback(() => {
    lastActivity.current = Date.now();
    arm();
  }, [arm]);

  // Throttled reset used by the noisy events (mousemove, scroll).
  const throttledReset = useCallback(() => {
    const now = Date.now();
    if (now - lastReset.current < RESET_THROTTLE_MS) return;
    lastReset.current = now;
    lastActivity.current = now;
    arm();
  }, [arm]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return undefined;
    }
    arm();
    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, throttledReset, { passive: true });
    });
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, throttledReset);
      });
      clearTimers();
    };
  }, [enabled, arm, throttledReset, clearTimers]);

  return {
    reset,
    getLastActivity: () => lastActivity.current,
  };
}

export default useIdleTimer;
